
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { 
    getFirestore, 
    doc, 
    getDoc, 
    setDoc, 
    serverTimestamp, 
    collection, 
    addDoc, 
    query, 
    where, 
    orderBy, 
    onSnapshot, 
    getDocFromServer, 
    deleteDoc,
    increment,
    updateDoc,
    getDocs
} from 'firebase/firestore';
import firebaseConfig from '../firebase-applet-config.json';

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);
export const googleProvider = new GoogleAuthProvider();

// Custom error info interface
export interface FirestoreErrorInfo {
  error: string;
  operationType: 'create' | 'update' | 'delete' | 'list' | 'get' | 'write';
  path: string | null;
  authInfo: {
    userId: string;
    email: string;
    emailVerified: boolean;
    isAnonymous: boolean;
    providerInfo: { providerId: string; displayName: string; email: string; }[];
  }
}

export const handleFirestoreError = (error: any, operationType: FirestoreErrorInfo['operationType'], path: string | null = null) => {
  if (error.code === 'permission-denied') {
    const user = auth.currentUser;
    const errorInfo: FirestoreErrorInfo = {
      error: error.message,
      operationType,
      path,
      authInfo: {
        userId: user?.uid || 'unauthenticated',
        email: user?.email || '',
        emailVerified: user?.emailVerified || false,
        isAnonymous: user?.isAnonymous || false,
        providerInfo: user?.providerData.map(p => ({
          providerId: p.providerId,
          displayName: p.displayName || '',
          email: p.email || ''
        })) || []
      }
    };
    throw new Error(JSON.stringify(errorInfo));
  }
  throw error;
};

// Authentication services
export const signInWithGoogle = async () => {
    try {
        const result = await signInWithPopup(auth, googleProvider);
        return result.user;
    } catch (error) {
        console.error("Error signing in with Google", error);
        throw error;
    }
};

export const logout = () => signOut(auth);

// User Profile services
export const syncUserProfile = async (user: User) => {
    const userDocRef = doc(db, 'users', user.uid);
    try {
        const userDoc = await getDoc(userDocRef);
        
        // IP Check logic
        let clientIp = 'unknown';
        try {
            const resp = await fetch('/api/client-info');
            const data = await resp.json();
            clientIp = data.ip || 'unknown';
        } catch (e) {
            console.error("IP check failed", e);
        }

        if (!userDoc.exists()) {
            const sanitizedIp = clientIp.replace(/[:.]/g, '_');
            const ipDocRef = doc(db, 'registration_ips', sanitizedIp);
            const ipDoc = await getDoc(ipDocRef);
            
            let startingCredits = 3;
            if (ipDoc.exists()) {
                const data = ipDoc.data();
                const existingUids = data?.uids || [];
                if (existingUids.length >= 2) {
                    // Prevent abuse: multiple accounts from same IP get 0 starting credits
                    startingCredits = 0;
                }
                if (!existingUids.includes(user.uid)) {
                    await setDoc(ipDocRef, {
                        uids: [...existingUids, user.uid],
                        updatedAt: serverTimestamp()
                    }, { merge: true });
                }
            } else {
                await setDoc(ipDocRef, {
                    uids: [user.uid],
                    ip: clientIp,
                    createdAt: serverTimestamp()
                });
            }

            await setDoc(userDocRef, {
                uid: user.uid,
                displayName: user.displayName,
                email: user.email,
                photoURL: user.photoURL,
                credits: startingCredits,
                registeredIp: clientIp,
                tier: 'free', // Default tier is free
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        } else {
            await setDoc(userDocRef, {
                updatedAt: serverTimestamp()
            }, { merge: true });
        }
    } catch (error) {
        handleFirestoreError(error, 'write', `users/${user.uid}`);
    }
};

export const logPaymentInterest = async (user: User) => {
    try {
        await addDoc(collection(db, 'payment_alerts'), {
            userId: user.uid,
            email: user.email,
            displayName: user.displayName,
            timestamp: serverTimestamp(),
            type: 'click_refill'
        });
    } catch (error) {
        console.error("Interest log error", error);
    }
};

// Credit services
export const deductCredit = async (userId: string, amount: number = 1) => {
    const userDocRef = doc(db, 'users', userId);
    try {
        await updateDoc(userDocRef, {
            credits: increment(-amount),
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        handleFirestoreError(error, 'update', `users/${userId}`);
    }
};

export const searchUserByEmail = async (email: string) => {
    try {
        const emailLower = email.toLowerCase().trim();
        const q = query(collection(db, 'users'), where('email', '==', emailLower));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            // Also try searching as-is in case it wasn't lowercased
            const qOrig = query(collection(db, 'users'), where('email', '==', email.trim()));
            const querySnapshotOrig = await getDocs(qOrig);
            if (querySnapshotOrig.empty) return null;
            const userDoc = querySnapshotOrig.docs[0];
            return { id: userDoc.id, ...userDoc.data() };
        }
        
        const userDoc = querySnapshot.docs[0];
        return { id: userDoc.id, ...userDoc.data() };
    } catch (error) {
        console.error("Search User Error:", error);
        handleFirestoreError(error, 'list', 'users');
        return null; // Should not reach here if handleFirestoreError throws
    }
};

export const addCredits = async (userId: string, amount: number) => {
    const userDocRef = doc(db, 'users', userId);
    try {
        await updateDoc(userDocRef, {
            credits: increment(amount),
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        handleFirestoreError(error, 'update', `users/${userId}`);
    }
};

export const updateUserTier = async (userId: string, tier: 'free' | 'standard' | 'premium' | 'vip') => {
    const userDocRef = doc(db, 'users', userId);
    try {
        await updateDoc(userDocRef, {
            tier,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        handleFirestoreError(error, 'update', `users/${userId}`);
    }
};

// History services
export const saveAudioHistory = async (userId: string, data: { text: string, voiceId: string, tone: string, seed?: number, audioData: string | null, isAudioTooLarge?: boolean }) => {
    try {
        const historyData: any = {
            text: data.text || '',
            voiceId: data.voiceId || 'Kore',
            tone: data.tone || 'standard',
            audioData: data.audioData || '',
            isAudioTooLarge: !!data.isAudioTooLarge,
            userId,
            createdAt: serverTimestamp()
        };
        
        // Paranoid check for seed
        if (typeof data.seed === 'number' && !isNaN(data.seed)) {
            historyData.seed = data.seed;
        }

        const colRef = collection(db, 'users', userId, 'history');
        await addDoc(colRef, historyData);
    } catch (error) {
        console.error("Firestore Save Error:", error);
        handleFirestoreError(error, 'create', `users/${userId}/history`);
    }
};

export const saveTranscriptionHistory = async (userId: string, transcribedText: string) => {
    try {
        await addDoc(collection(db, 'users', userId, 'transcriptions'), {
            userId,
            transcribedText,
            createdAt: serverTimestamp()
        });
    } catch (error) {
        handleFirestoreError(error, 'create', `users/${userId}/transcriptions`);
    }
};

export const deleteHistoryItem = async (userId: string, itemId: string, type: 'history' | 'transcriptions') => {
    try {
        await deleteDoc(doc(db, 'users', userId, type, itemId));
    } catch (error) {
        handleFirestoreError(error, 'delete', `users/${userId}/${type}/${itemId}`);
    }
};

// Check connection on boot
export async function testConnection() {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    if(error instanceof Error && error.message.includes('the client is offline')) {
      console.error("Please check your Firebase configuration.");
    }
  }
}
testConnection();
